import { ReactNode } from "react";

import { AccessDenied } from "@/components/common/AccessDenied";
import { useIsAdminReliable } from "@/hooks/useIsAdminReliable";

import {
  getRouteGuardWrapper,
  RouteGuardMode,
  RouteGuardState,
  useRouteGuard,
} from "./registry";

export interface RouteGuardProps {
  mode: RouteGuardMode;
  policy?: string;
  children: ReactNode;
  renderLoading?: (state: RouteGuardState) => ReactNode;
  renderBlocked?: (state: RouteGuardState) => ReactNode;
}

type GuardOverlayStatus = "loading" | "blocked";

function GuardOverlay({
  status,
  children,
}: {
  status: GuardOverlayStatus;
  children: ReactNode;
}) {
  return (
    <main
      role="main"
      className="min-h-screen w-full bg-background"
      aria-busy={status === "loading" ? true : undefined}
      aria-live={status === "blocked" ? "assertive" : "polite"}
    >
      <div className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  );
}

export function RouteGuard({
  mode,
  policy,
  children,
  renderLoading,
  renderBlocked,
}: RouteGuardProps) {
  const state = useRouteGuard(mode, { policy });
  const adminAccess = useIsAdminReliable({ forceEnable: mode === "admin" });

  // CRITICAL: Always render children to prevent React error #300
  // Instead of mount/unmount, use overlays for loading/blocked states
  const Wrapper = getRouteGuardWrapper(mode);
  
  const childrenContent = Wrapper ? <Wrapper>{children}</Wrapper> : <>{children}</>;

  // Show loading/blocked overlays without unmounting children
  if (state.status === "loading") {
    const loadingContent = renderLoading ? renderLoading(state) : state.view;
    return (
      <>
        <GuardOverlay status="loading">{loadingContent}</GuardOverlay>
        <div style={{ display: "none" }} aria-hidden="true" hidden>
          {childrenContent}
        </div>
      </>
    );
  } else if (state.status === "blocked") {
    const blockedContent = renderBlocked ? renderBlocked(state) : state.view;
    return (
      <>
        <GuardOverlay status="blocked">{blockedContent}</GuardOverlay>
        <div style={{ display: "none" }} aria-hidden="true" hidden>
          {childrenContent}
        </div>
      </>
    );
  }

  if (mode === "admin") {
    // 🛡️ DEBUG: Log RouteGuard admin check
    if (typeof window !== 'undefined') {
      console.log('[RouteGuard] Admin mode check:', {
        stateStatus: state.status,
        adminAccessIsLoading: adminAccess.isLoading,
        adminAccessCanPerform: adminAccess.canPerformAdminActions,
        adminAccessIsAdmin: adminAccess.isAdmin,
      });
    }

    if (adminAccess.isLoading) {
      const adminLoadingState = {
        ...state,
        status: "loading" as const,
        meta: {
          ...state.meta,
          admin: {
            isAdmin: adminAccess.isAdmin,
            canPerformAdminActions: adminAccess.canPerformAdminActions,
            isLoading: adminAccess.isLoading,
            failureCount: adminAccess.failureCount,
            lastErrorCode: adminAccess.lastErrorCode,
            error: state.meta.admin?.error ?? null,
          },
        },
      };
      const loadingContent = renderLoading
        ? renderLoading(adminLoadingState)
        : adminLoadingState.view ?? (
            <div className="min-h-screen flex items-center justify-center bg-background">
              <p className="text-sm text-muted-foreground">Checking admin access…</p>
            </div>
          );

      return (
        <>
          <GuardOverlay status="loading">{loadingContent}</GuardOverlay>
          <div style={{ display: "none" }} aria-hidden="true" hidden>
            {childrenContent}
          </div>
        </>
      );
    }

    if (!adminAccess.canPerformAdminActions) {
      // 🛡️ DEBUG: Log why RouteGuard blocks access
      if (typeof window !== 'undefined') {
        console.warn('[RouteGuard] Blocking access - useIsAdminReliable returned false:', {
          isAdmin: adminAccess.isAdmin,
          canPerformAdminActions: adminAccess.canPerformAdminActions,
          isLoading: adminAccess.isLoading,
          lastErrorCode: adminAccess.lastErrorCode,
          userId: adminAccess.userId,
        });
      }

      const adminBlockedState = {
        ...state,
        status: "blocked" as const,
        view: <AccessDenied title="Admin Access Required" message="You must be in the staff roster to open Ops Center." />,
        meta: {
          ...state.meta,
          admin: {
            isAdmin: false,
            canPerformAdminActions: false,
            isLoading: false,
            failureCount: adminAccess.failureCount,
            lastErrorCode: adminAccess.lastErrorCode,
            error: state.meta.admin?.error ?? null,
          },
        },
      };

      const blockedContent = renderBlocked ? renderBlocked(adminBlockedState) : adminBlockedState.view;

      return (
        <>
          <GuardOverlay status="blocked">{blockedContent}</GuardOverlay>
          <div style={{ display: "none" }} aria-hidden="true" hidden>
            {childrenContent}
          </div>
        </>
      );
    }
  }

  return childrenContent;
}

export type { RouteGuardState, RouteGuardMeta } from "./registry";
export { useRouteGuard } from "./registry";

